import { COST_EVALUATOR_NAME, LATENCY_EVALUATOR_NAME, TOKENS_EVALUATOR_NAME } from "@/services/evaluations.service";
import { Evaluator, EvaluatorJudgmentOption } from "@/types/app/evaluator";
import { EvaluatorLog } from "@/types/app/log";
import { EvaluatorAggregate } from "@/types/app/version";
import { Tag } from "@components/library/Tag";
import { XCircleIcon } from "@heroicons/react/outline";
import { None } from "../Tables/Logs/LogCells";
import { LogPreview } from "./EvaluatorEditor/DebugConsole/LogPreview";
import { ArrowUpRightIcon } from "@components/library/Icons";
import { formatNumberPercentage, formatNumberPrecise } from "@/lib/utils";

export interface EvaluationResultProps {
  evaluatorLog: EvaluatorLog;
  onFeedbackChange?: (evaluatorLog: EvaluatorLog) => void;
  customAction?: () => void;
}

/** Renders the result of an evaluation.
 *
 * This is a Tag, number or a tag that says error with a tooltip. One line span.
 */
export const EvaluationValueWithPopover = ({ evaluatorLog, onFeedbackChange, customAction }: EvaluationResultProps) => {
  return (
    <LogPreview evaluatorLog={evaluatorLog} customAction={customAction}>
      {/* FYI i tried a few ways of making it clearer that this is hoverable / clickable including
      adding an arrow icon or the Log Icon... all looked too busy. The underline isn't consistent though,
      so should revisit the design. */}
      <div className="flex items-center gap-4 border-b border-stroke-secondary-2 pb-2 pt-1 hover:border-stroke-secondary-3">
        <JudgmentTags evaluatorLog={evaluatorLog} evaluator={evaluatorLog.evaluator} />
      </div>
    </LogPreview>
  );
};

export function renderAggregateValue(
  evaluator: Evaluator,
  value: null,
  fallbackNumberFormatOptions?: Intl.NumberFormatOptions,
): { displayValue: React.ReactNode; value: null };
export function renderAggregateValue(
  evaluator: Evaluator,
  value: number,
  fallbackNumberFormatOptions?: Intl.NumberFormatOptions,
): { displayValue: React.ReactNode; value: number };
export function renderAggregateValue(
  evaluator: Evaluator,
  value: number | null,
  fallbackNumberFormatOptions?: Intl.NumberFormatOptions,
): { displayValue: React.ReactNode; value: number | null };
export function renderAggregateValue(
  evaluator: Evaluator,
  value: number | null,
  fallbackNumberFormatOptions: Intl.NumberFormatOptions = {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
): { displayValue: React.ReactNode; value: number | null } {
  let displayValue: string = "";
  let roundedValue: number | null = null;
  if (value === null) {
    return { displayValue: <None />, value: null };
  }
  const evaluatorName = evaluator.name;

  if (evaluatorName === LATENCY_EVALUATOR_NAME) {
    roundedValue = Number(value.toFixed(4));
    displayValue = `${formatNumberPrecise(roundedValue)} s`;
  } else if (evaluatorName === COST_EVALUATOR_NAME) {
    roundedValue = Number(value.toFixed(4));
    displayValue = `$ ${formatNumberPrecise(roundedValue, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  } else if (evaluatorName === TOKENS_EVALUATOR_NAME) {
    roundedValue = Math.round(Number(value)); // Rounds to nearest integer
    displayValue = `${roundedValue}`;
  } else if (evaluator.spec.return_type === "boolean") {
    displayValue = formatNumberPercentage(value);
    roundedValue = Number(value);
  } else if (evaluator.spec.return_type === "number") {
    displayValue = Number(value).toLocaleString(undefined, fallbackNumberFormatOptions);
    roundedValue = Number(value.toFixed(fallbackNumberFormatOptions.maximumFractionDigits ?? 0));
  } else {
    displayValue = value.toString();
    roundedValue = value;
  }
  return { displayValue, value: roundedValue };
}

/** Renders the judgment of an evaluation. */
export const JudgmentTags = ({
  evaluatorLog,
  evaluator,
}: {
  evaluatorLog: Pick<EvaluatorLog, "judgment" | "error">;
  evaluator: Evaluator;
}) => {
  const returnType = evaluator.spec.return_type;

  if (evaluatorLog.error !== null) {
    return <ErrorValue />;
  }

  if (evaluatorLog.judgment === null || evaluatorLog.judgment === undefined) {
    return <None />;
  }

  switch (returnType) {
    case "boolean":
      return <BooleanValue value={evaluatorLog.judgment as boolean} />;
    case "number":
      return <NumberValue value={evaluatorLog.judgment as number} />;
    case "select":
      return (
        <SelectValue
          value={evaluatorLog.judgment as string}
          options={"options" in evaluator.spec ? evaluator.spec.options : []}
        />
      );
    case "multi_select":
      return (
        <MultiSelectValue
          values={evaluatorLog.judgment as string[]}
          options={"options" in evaluator.spec ? evaluator.spec.options : []}
        />
      );
    case "text":
      return <TextValue value={evaluatorLog.judgment as string} />;
    default:
      return <None />;
  }
};

const ErrorValue = () => (
  <div className="flex h-20 items-center space-x-4">
    <XCircleIcon className="h-16 w-16 text-oldred-600" />
    <span className="text-xs text-oldred-600">Error</span>
  </div>
);

const BooleanValue = ({ value }: { value: boolean }) => {
  const { text, color } = value ? { text: "True", color: "green" as const } : { text: "False", color: "red" as const };

  return (
    <div className="flex items-center gap-6">
      <Tag size={20} color={color}>
        {text}
      </Tag>
    </div>
  );
};

const NumberValue = ({ value }: { value: number }) => (
  <div className="flex h-20 items-center truncate font-mono text-13-20 tabular-nums">
    {value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}
  </div>
);

const SelectValue = ({ value, options }: { value: string; options?: EvaluatorJudgmentOption[] | null }) => {
  const valence = options?.find((o) => o.name === value)?.valence || "neutral";
  return (
    <Tag color={valence === "positive" ? "green" : valence === "negative" ? "red" : "gray"}>
      <div className="truncate">{value}</div>
    </Tag>
  );
};

const MultiSelectValue = ({ values, options }: { values: string[]; options?: EvaluatorJudgmentOption[] | null }) => {
  return (
    <div className="flex flex-wrap gap-4">
      {values.map((v, i) => (
        <SelectValue key={v} value={v} options={options} />
      ))}
    </div>
  );
};

const TextValue = ({ value }: { value: string }) => (
  <div className="text-left text-13-20 text-text-base-2">{value}</div>
);
